import { useMemo } from "react";
import { createPortal } from "react-dom";
import {
  AssessmentQuestionResponses,
  AssessmentType,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import ComboboxGeneric from "@components/combobox/generic-combobox";
import FormFields from "@components/form/form-fields";
import Input from "@components/input/input";
import Modal, { ModalWidth } from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { assertNonNull } from "@helpers/helpers";

import {
  ProgramFormCoreValueReviewConfig,
  ProgramFormStepDownwardReview,
  ProgramFormStepPeerReview,
} from "../types";

const scaleSizeOptions = (startsAt: number) => [
  {
    value: 2,
    label: `${1 + startsAt}`,
  },
  {
    value: 3,
    label: `${2 + startsAt}`,
  },
  {
    value: 4,
    label: `${3 + startsAt}`,
  },
  {
    value: 5,
    label: `${4 + startsAt}`,
  },
];

const scaleStartsAtOptions = [
  {
    value: 0,
    label: "0",
  },
  {
    value: 1,
    label: "1",
  },
];

const CoreValueAssessmentSettingsModal = ({
  assessmentType,
  onClose,
  canUpdate,
  selectedPerformanceAssessmentGroup,
  onChangeSettings,
}: {
  selectedPerformanceAssessmentGroup:
    | ProgramFormStepPeerReview
    | ProgramFormStepDownwardReview;
  canUpdate: boolean;
  onClose: () => void;
  onChangeSettings: (
    settings: Partial<ProgramFormCoreValueReviewConfig>,
  ) => void;
  assessmentType: AssessmentType;
}) => {
  const label = useLabel();

  const responsesOptions = useMemo(
    () => [
      {
        label: "Both Manager and Subject",
        description: `Everyone answers Values questions`,
        value: AssessmentQuestionResponses.Both,
      },
      {
        label: "Manager Only",
        description: "Only the manager of the subject answers Values questions",
        value: AssessmentQuestionResponses.ExcludeSelfAssessment,
      },
    ],
    [],
  );

  const config = selectedPerformanceAssessmentGroup.coreValueReviewConfig;

  return createPortal(
    <Modal
      open
      width={ModalWidth.medium}
      onClose={onClose}
      aria-label="Core value assessment settings modal"
    >
      <div className="flex flex-col">
        <ModalTitle className="p-6 border-b">{`Values ${label("review", {
          capitalize: true,
        })}`}</ModalTitle>

        {assessmentType === AssessmentType.Performance && (
          <div className="p-6 border-b">
            <FormFields
              width={400}
              fields={[
                {
                  title: "Responders",
                  render: () => (
                    <ComboboxGeneric
                      options={responsesOptions}
                      disabled={!canUpdate}
                      onChangeValue={(opt) =>
                        onChangeSettings({
                          responses: opt.value,
                        })
                      }
                      value={assertNonNull(
                        responsesOptions.find(
                          ({ value }) => value === config.responses,
                        ),
                      )}
                    />
                  ),
                },
              ]}
            />
          </div>
        )}

        <div className="p-6 border-b">
          <div className="font-bold mb-6 text-sm">Input</div>
          <FormFields
            width={400}
            fields={[
              {
                title: "Scale",
                render: () => (
                  <div className="flex gap-2 items-center">
                    <ComboboxGeneric
                      containerClassName="w-24"
                      options={scaleStartsAtOptions}
                      disabled={!canUpdate}
                      onChangeValue={(opt) =>
                        onChangeSettings({
                          assessmentScaleStartsAt: opt.value,
                        })
                      }
                      value={assertNonNull(
                        scaleStartsAtOptions.find(
                          ({ value }) =>
                            value === config.assessmentScaleStartsAt,
                        ),
                      )}
                    />
                    <TopicflowIcon
                      type="rightArrow"
                      className="text-gray-500 w-4 h-4 shrink-0"
                    />
                    <ComboboxGeneric
                      containerClassName="w-24"
                      options={scaleSizeOptions(
                        assertNonNull(config.assessmentScaleStartsAt),
                      )}
                      disabled={!canUpdate}
                      onChangeValue={(opt) =>
                        onChangeSettings({
                          assessmentScale: opt.value,
                          assessmentScaleLabels:
                            config.assessmentScaleLabels.length > opt.value
                              ? config.assessmentScaleLabels.slice(0, opt.value)
                              : config.assessmentScaleLabels.length < opt.value
                                ? config.assessmentScaleLabels.concat(
                                    Array(
                                      opt.value -
                                        config.assessmentScaleLabels.length,
                                    ).fill(""),
                                  )
                                : config.assessmentScaleLabels,
                        })
                      }
                      value={assertNonNull(
                        scaleSizeOptions(
                          assertNonNull(config.assessmentScaleStartsAt),
                        ).find(({ value }) => value === config.assessmentScale),
                      )}
                    />
                  </div>
                ),
              },
              ...assertNonNull(config.assessmentScaleLabels).map(
                (label, index) => {
                  return {
                    title: `Label ${
                      index + assertNonNull(config.assessmentScaleStartsAt)
                    }`,
                    render: () => (
                      <Input
                        disabled={!canUpdate}
                        placeholder="Label (optional)"
                        value={label ?? ""}
                        onChange={(e) => {
                          const newLabels = [
                            ...assertNonNull(config.assessmentScaleLabels),
                          ];
                          newLabels[index] = e.target.value;
                          onChangeSettings({
                            assessmentScaleLabels: newLabels,
                          });
                        }}
                      />
                    ),
                  };
                },
              ),
            ]}
          />
        </div>

        <div className="p-6 flex items-center justify-end">
          <Button
            onClick={onClose}
            theme={buttonTheme.primary}
            text="Save changes"
          />
        </div>
      </div>
    </Modal>,
    document.body,
  );
};

export default CoreValueAssessmentSettingsModal;
